"use client";

import React, { useMemo } from "react";
import Image from "next/image";
import { SliderRowH } from "@/components/SliderRowH";
import MixedFontText from "@/components/MixedFontText";
import SectionOne, { StatItem } from "@/components/Sections/SectionOne";
import ScrollHeader from "@/components/Layouts/ScrollHeader";
import { useScrollProgress } from "@/hooks/useScrollProgress";
import ActionButton from "@/components/Buttons/ActionButton";
import { SliderRowW } from "@/components/SliderRowW";
import Section3, { Section3Item } from "@/components/Sections/Section3";
import Section4 from "@/components/Sections/Section4";
import SectionPartners, {
  PartnerGroup,
} from "@/components/Sections/SectionPartners";
import FooterContact, { INFO } from "@/components/Layouts/FooterContact";

import PopupSlider from "@/components/PopupSlider";
import appendImageUrlFromAPI from "@/utils/appendImageUrlFromAPI";
import DefaultSEO from "@/components/DefaultSEO";
import sanitizeDOMData from "@/utils/sanitizeDOMData";
import { AddOnItem } from "@/models/addOnItem";
import { parseHtmlTextToJson } from "@/utils/parseHtmlTextToJson";
import { toBoolean } from "@/utils/generalFunctions";
import BusinessSlider from "@/components/Sections/BusinessSlider";
import { useGetProcesses } from "@/apis/queryFunctions/processFunctions";
import { mapProcessesToBusinessFields } from "@/utils/parseProcessData";
import Section5Video from "@/components/Sections/Section5Video";
interface HomeProps {
  seo: any;
  data: any;
  locale: string;
}
type ApiResponse = {
  add_on_data_3?: AddOnItem[];
};
export default function HomeClient({ data }: HomeProps) {
  const progress = useScrollProgress();
  const shadowOpacity = Math.min(progress * 4, 1);

  const scrollToContact = () => {
    const el = document.getElementById("contact");
    if (!el) return;

    el.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };
  const IMAGES = [
    appendImageUrlFromAPI({ src: data?.picture, size: "2k" }),
    appendImageUrlFromAPI({ src: data?.picture_2, size: "2k" }),
    appendImageUrlFromAPI({ src: data?.picture_3, size: "2k" }),
  ];
  const IMAGE1 = [
    appendImageUrlFromAPI({ src: data?.add_on_data?.[0]?.picture, size: "xl" }),
    appendImageUrlFromAPI({
      src: data?.add_on_data?.[0]?.picture_2,
      size: "xl",
    }),
    appendImageUrlFromAPI({
      src: data?.add_on_data?.[0]?.picture_3,
      size: "xl",
    }),
    appendImageUrlFromAPI({
      src: data?.add_on_data?.[0]?.picture_4,
      size: "xl",
    }),
    appendImageUrlFromAPI({
      src: data?.add_on_data?.[0]?.picture_5,
      size: "xl",
    }),
    appendImageUrlFromAPI({
      src: data?.add_on_data?.[0]?.picture_6,
      size: "xl",
    }),
  ];
  const IMAGE2 = [
    appendImageUrlFromAPI({ src: data?.add_on_data?.[0]?.picture, size: "xl" }),
    appendImageUrlFromAPI({
      src: data?.add_on_data?.[1]?.picture_2,
      size: "xl",
    }),
    appendImageUrlFromAPI({
      src: data?.add_on_data?.[1]?.picture_3,
      size: "xl",
    }),
    appendImageUrlFromAPI({
      src: data?.add_on_data?.[1]?.picture_4,
      size: "xl",
    }),
    appendImageUrlFromAPI({
      src: data?.add_on_data?.[1]?.picture_5,
      size: "xl",
    }),
    appendImageUrlFromAPI({
      src: data?.add_on_data?.[1]?.picture_6,
      size: "xl",
    }),
  ];
  const IMAGE3 = [
    appendImageUrlFromAPI({ src: data?.add_on_data?.[0]?.picture, size: "xl" }),
    appendImageUrlFromAPI({
      src: data?.add_on_data?.[2]?.picture_2,
      size: "xl",
    }),
    appendImageUrlFromAPI({
      src: data?.add_on_data?.[2]?.picture_3,
      size: "xl",
    }),
    appendImageUrlFromAPI({
      src: data?.add_on_data?.[2]?.picture_4,
      size: "xl",
    }),
    appendImageUrlFromAPI({
      src: data?.add_on_data?.[2]?.picture_5,
      size: "xl",
    }),
    appendImageUrlFromAPI({
      src: data?.add_on_data?.[2]?.picture_6,
      size: "xl",
    }),
  ];
  const IMAGE4 = [
    appendImageUrlFromAPI({ src: data?.add_on_data?.[0]?.picture, size: "xl" }),
    appendImageUrlFromAPI({
      src: data?.add_on_data?.[5]?.picture_2,
      size: "xl",
    }),
    appendImageUrlFromAPI({
      src: data?.add_on_data?.[5]?.picture_3,
      size: "xl",
    }),
    appendImageUrlFromAPI({
      src: data?.add_on_data?.[5]?.picture_4,
      size: "xl",
    }),
    appendImageUrlFromAPI({
      src: data?.add_on_data?.[5]?.picture_5,
      size: "xl",
    }),
    appendImageUrlFromAPI({
      src: data?.add_on_data?.[5]?.picture_6,
      size: "xl",
    }),
  ];
  const STATS: StatItem[] = [
    {
      title: data?.add_on_data_2?.[0]?.title || "Tổng quy mô dự án",
      value: data?.add_on_data_2?.[0]?.content_3 || 97.35,
      suffix: data?.add_on_data_2?.[0]?.summary || " ha",
      image: appendImageUrlFromAPI({
        src: data?.add_on_data_2?.[0]?.picture,
        size: "xl",
      }),
      useGrouping: toBoolean(data?.add_on_data_2?.[0]?.content_2),
    },
    {
      title: data?.add_on_data_2?.[1]?.title || "Hoàn thiện hạ tầng kết nối",
      value: data?.add_on_data_2?.[1]?.content_3 || 2026,
      image: appendImageUrlFromAPI({
        src: data?.add_on_data_2?.[1]?.picture,
        size: "xl",
      }),
      useGrouping: toBoolean(data?.add_on_data_2?.[1]?.content_2),
    },
    {
      title: data?.add_on_data_2?.[2]?.title || "Nhu cầu thực từ KCN lân cận",
      value: data?.add_on_data_2?.[2]?.content_3 || 50000,
      suffix: data?.add_on_data_2?.[2]?.summary || " người",
      image: appendImageUrlFromAPI({
        src: data?.add_on_data_2?.[2]?.picture,
        size: "xl",
      }),
      useGrouping: toBoolean(data?.add_on_data_2?.[2]?.content_2),
    },
    {
      title: data?.add_on_data_2?.[3]?.title || "Sổ hồng sở hữu lâu dài",
      value: data?.add_on_data_2?.[3]?.content_3 || 100,
      suffix: data?.add_on_data_2?.[3]?.summary || "%",
      image: appendImageUrlFromAPI({
        src: data?.add_on_data_2?.[3]?.picture,
        size: "xl",
      }),
      useGrouping: toBoolean(data?.add_on_data_2?.[3]?.content_2),
    },
  ];

  const parseSection3Items = (addOnData3?: AddOnItem[]): Section3Item[] => {
    if (!Array.isArray(addOnData3)) return [];

    return addOnData3.map((item) => ({
      id: item.title, // theo yêu cầu: id = title
      title: item.summary, // summary
      description: item.content, // content (HTML string)
      image: appendImageUrlFromAPI({ src: item.picture, size: "xl" }), // picture
      tab: item.content_3,
    }));
  };

  const regions = parseHtmlTextToJson(data?.add_on_data?.[6]?.content);

  const parseSection5Items = (
    item1?: AddOnItem,
    item2?: AddOnItem,
    item3?: AddOnItem,
    item4?: AddOnItem,
  ): PartnerGroup[] => {
    const items = [item1, item2, item3, item4];

    return items
      .map((item, index) => {
        if (!item) return null;

        const logos = [
          item.picture,
          item.picture_2,
          item.picture_3,
          item.picture_4,
          item.picture_5,
          item.picture_6,
        ]
          .filter(Boolean)
          .map((src) =>
            appendImageUrlFromAPI({
              src: src as string,
              size: "xl",
            }),
          );

        return {
          id: item.title || `partner-${index + 1}`,
          title: item.summary,
          logos,
          maxHeightClass: item.content_3 || "max-h-[80px]",
        };
      })
      .filter(Boolean) as PartnerGroup[];
  };

  const { data: processesData } = useGetProcesses({
    ngon_ngu: "vi",
  });
  const businessFields = useMemo(
    () => mapProcessesToBusinessFields(processesData),
    [processesData],
  );

  const processDescription = data?.add_on_data?.[15]?.content || "";
  const processTitle = data?.add_on_data?.[15]?.title || "";

  const FOOTERINFO: INFO = {
    title: data?.add_on_data?.[12]?.title || "Liên hệ tư vấn",
    description1:
      data?.add_on_data?.[12]?.content ||
      "Ora Park – Khu đô thị xanh chuẩn mực, nơi kiến tạo cuộc sống cân bằng giữa thiên nhiên, tiện ích hiện đại và cộng đồng văn minh.",
    title2: data?.add_on_data?.[12]?.content_3 || "Địa chỉ",
    description2:
      data?.add_on_data?.[12]?.content_2 ||
      "Khu dân cư Ngọc Long, Đức Hòa Đông, Đức Hòa, Long An, nay là Mỹ Hạnh, Tây Ninh.",
    title3: data?.add_on_data?.[13]?.title || "liên hệ",
    description3:
      data?.add_on_data?.[13]?.content ||
      "Quý khách hàng vui lòng để lại thông tin, ORA Park sẽ liên hệ và hỗ trợ trong thời gian sớm nhất.",
    phone: data?.add_on_data?.[13]?.summary || "Hotline: 070 809 22 68",
    mail: data?.add_on_data?.[13]?.content_3 || "Email: onevision@gmail.com",
    webUrl: data?.add_on_data?.[13]?.content_2 || "Website: orapark.vn",
    fb: data?.add_on_data?.[14]?.summary || "https://www.facebook.com/orapark/",
    youtube:
      data?.add_on_data?.[14]?.content_3 ||
      "https://www.youtube.com/@OneVisionGroup-VietNam",
  };

  return (
    <>
      <h1 className="sr-only">
        {data?.meta_title ||
          "ORA PARK - Nơi mỗi khoảnh khắc sống đều trong trẻo."}
      </h1>
      <PopupSlider IMAGES={IMAGES} />
      <ScrollHeader fb={FOOTERINFO.fb} mail={FOOTERINFO.mail} />
      {/* ANCHOR để scroll */}
      <div id="banner-anchor" className="h-[1px] w-full" />
      {/* BANNER (FIXED) */}
      <section id="banner" className="fixed inset-0 z-0">
        <div className="relative w-full h-screen bg-secondary overflow-hidden">
          {/* BACKGROUND SLIDERS */}
          <div
            className="
        absolute inset-0
        flex flex-col justify-center gap-8
        px-12 md:px-24 py-24
        rotate-[-20deg] scale-125
      "
          >
            <SliderRowH images={IMAGE1} direction="ltr" />
            <SliderRowH images={IMAGE2} direction="rtl" />
            <SliderRowH images={IMAGE3} direction="ltr" />
          </div>

          {/* CINEMATIC MASK */}
          <div className="pointer-events-none absolute inset-0 z-mask">
            <div className="absolute inset-x-0 top-0 h-1/3 bg-gradient-to-b from-black/50 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/70 to-transparent" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,0)_20%,rgba(0,0,0,0.45)_100%)]" />
          </div>

          {/* DARK OVERLAY */}
          <div className="pointer-events-none absolute inset-0 z-mask bg-black/15" />

          {/* CONTENT WRAPPER */}
          <div className="relative z-mask flex items-center justify-center h-full">
            {/* HEADER ON BANNER */}
            <div
              className="
          absolute top-0 left-0 right-0 z-under_header
          px-4 py-3
          md:px-16 md:py-6
        "
            >
              <div
                className="
            grid grid-cols-3 items-start
            px-2 pt-2
            md:px-20 md:pt-6
          "
              >
                {/* LEFT ICONS */}
                <div className="flex items-center gap-3 justify-start pt-1 md:pt-3">
                  {FOOTERINFO.fb && FOOTERINFO.mail && (
                    <>
                      <a
                        href={FOOTERINFO.fb}
                        className="opacity-70 hover:opacity-100 transition"
                      >
                        <img
                          src="/icons/fb_icon_white.svg"
                          className="h-5 md:h-6"
                          alt="fb"
                        />
                      </a>
                      <a
                        href={FOOTERINFO.mail}
                        className="opacity-70 hover:opacity-100 transition"
                      >
                        <img
                          src="/icons/email_icon_white.svg"
                          className="h-5 md:h-6"
                          alt="email"
                        />
                      </a>
                    </>
                  )}
                </div>

                {/* LOGO */}
                <div className="flex justify-center pointer-events-none">
                  <div className="relative aspect-square h-[172px] md:h-[200px] lg:h-[300px] mt-21 sm:mt-18 md:mt-0">
                    <Image
                      src="/logos/logo_white_with_name.png"
                      alt="Logo"
                      fill
                      sizes="(min-width: 1024px) 50vw, (min-width: 768px) 70vw, 90vw"
                      priority
                      className="object-contain"
                    />
                  </div>
                </div>

                {/* CONTACT */}
                <div className="flex items-center gap-4 justify-end pt-1 md:pt-3">
                  <button
                    type="button"
                    onClick={scrollToContact}
                    className="
                text-white/90 text-[10px] md:text-xs
                tracking-[0.25em] md:tracking-[0.3em]
                hover:text-white transition
                cursor-pointer
              "
                  >
                    LIÊN HỆ
                  </button>
                </div>
              </div>
            </div>

            {/* HERO TEXT */}
            <div
              className="
          absolute
          bottom-24 md:bottom-20
          left-4 right-4
          md:left-10 md:right-10
          px-4 md:px-20
        "
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-12 items-start md:items-end">
                {/* LEFT */}
                <div className="text-white">
                  <h3
                    className="
                font-bold tracking-widest mb-2
                text-xs sm:text-sm lg:text-base xl:text-xl
                line-clamp-3 md:line-clamp-none
              "
                  >
                    {data?.add_on_data?.[0]?.summary}
                  </h3>

                  <div
                    className="
                max-w-lg opacity-90
                text-xs sm:text-sm xl:text-base
                line-clamp-4 md:line-clamp-none
              "
                  >
                    {sanitizeDOMData(data?.add_on_data?.[0]?.content)}
                  </div>
                </div>

                {/* RIGHT */}
                <div
                  className="
              text-right md:text-right
              text-white
              space-y-1 md:space-y-4
              text-xs sm:text-sm lg:text-sm xl:text-base
            "
                >
                  <div className="line-clamp-2 md:line-clamp-none">
                    <MixedFontText text={data?.add_on_data?.[0]?.content_3} />
                  </div>

                  <div className="line-clamp-2 md:line-clamp-none">
                    <MixedFontText text={data?.add_on_data?.[0]?.content_2} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SCROLL SPACER */}
      <div className="h-screen" id="section-one-trigger" />

      {/* SCROLLABLE CONTENT */}
      <div className="relative z-under_header">
        <SectionOne
          bgImg={appendImageUrlFromAPI({
            src: data?.add_on_data?.[3]?.picture,
            size: "xl",
          })}
          STATS={STATS}
          radius={0}
          shadowOpacity={shadowOpacity}
        />

        <section className="relative bg-white py-28 md:py-36">
          <div className="max-w-7xl mx-auto px-8 md:px-16">
            {/* TEXT ROW */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center mb-24">
              {/* LEFT – SLOGAN */}
              <div className="flex flex-col space-y-1 md:space-y-1">
                <MixedFontText
                  text={
                    data?.add_on_data?.[4]?.title ||
                    "Nơi mỗi khoảnh khắc sống đều "
                  }
                  wrapperClassName="text-black/80 text-left max-w-[520px]"
                  normalClassName="text-2xl md:text-3xl"
                  highlightClassName="text-3xl md:text-4xl font-bold"
                />
                <MixedFontText
                  text={data?.add_on_data?.[4]?.summary || " **TRONG TRẺO**."}
                  wrapperClassName="text-black/80  text-left max-w-[520px] ml-10"
                  normalClassName="text-2xl md:text-3xl"
                  highlightClassName="text-3xl md:text-4xl font-bold text-primary"
                />
                <MixedFontText
                  text={
                    data?.add_on_data?.[4]?.content_3 ||
                    "Khoảng xanh, tiện ích và kết nối được tạo ra cho những gia đình tìm kiếm sự "
                  }
                  wrapperClassName="text-black/80 text-left max-w-[520px]"
                  normalClassName="text-2xl md:text-3xl"
                  highlightClassName="text-3xl md:text-4xl font-bold"
                />
                <MixedFontText
                  text={
                    data?.add_on_data?.[4]?.content_2 ||
                    "**BÌNH YÊN** trong đô thị."
                  }
                  wrapperClassName="text-black/80 text-right max-w-[620px]"
                  normalClassName="text-2xl md:text-3xl"
                  highlightClassName="text-3xl md:text-4xl font-bold text-primary"
                />
              </div>

              {/* RIGHT – DESCRIPTION */}
              <div className="space-y-8">
                <div className="text-black/70 leading-relaxed text-sm md:text-sm">
                  {sanitizeDOMData(data?.add_on_data?.[4]?.content)}
                </div>

                <ActionButton
                  label="LIÊN HỆ"
                  onClick={scrollToContact}
                  className="font-semibold uppercase tracking-wide bg-[#6B5542] text-white transition shadow-[0_10px_30px_rgba(0,0,0,0.25)]"
                />
              </div>
            </div>
          </div>

          {/* SLIDER */}
          <div className="relative">
            <SliderRowW images={IMAGE4} direction="ltr" />
          </div>
        </section>
        <Section3 SECTION3_ITEMS={parseSection3Items(data?.add_on_data_3)} />
        <Section4
          image={data?.add_on_data?.[6]?.picture}
          MAP_REGIONS={regions}
        />
        <Section5Video
          videoUrl={data?.add_on_data?.[7]?.summary}
          backgroundImage={appendImageUrlFromAPI({
            src: data?.add_on_data?.[7]?.picture,
            size: "xl",
          })}
        />
        <BusinessSlider
          businessFields={businessFields}
          desc={processDescription}
          title={processTitle}
        />
        <SectionPartners
          PARTNER_GROUPS={parseSection5Items(
            data?.add_on_data?.[8],
            data?.add_on_data?.[9],
            data?.add_on_data?.[10],
            data?.add_on_data?.[11],
          )}
        />
        <FooterContact INFO={FOOTERINFO} />
      </div>
    </>
  );
}
